import { graphql } from "babel-plugin-relay/macro";
import { Suspense, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFragment, useLazyLoadQuery, useQueryLoader } from "react-relay";
import { Navigate, useMatch } from "react-router-dom";
import styled from "styled-components";
import { NodeScreen_NodeFragment$key } from "@generated/NodeScreen_NodeFragment.graphql";
import { NodeScreen_Query } from "@generated/NodeScreen_Query.graphql";
import { usePageTitle } from "../../infecto-lms-webapp/hooks/usePageTitle";
import { selectProgressBar } from "../../infecto-lms-webapp/redux/slices/progress-bar.slice";
import { ProjectUserStatusMessageComponent } from "../../infecto-lms-webapp-impl/components/ProjectUserStatusMessage.component";
import {
	getLearnOpportunityPath,
	LEARN_OPPORTUNITY_ID_URL_PARAM,
} from "../../infecto-lms-webapp-impl/router/routes/auth/nodes.routes";
import { NodeLeftFrame } from "../components/left-frame/NodeLeftFrame.component";
import { ShowAnswerButtonContainer } from "../components/node-screen/parts/show-answer-button-container/show-answer-button.container";
import { RightFrameContainer } from "../components/right-frame/RightFrameContainer";
import { SkeletonRightFrame } from "../components/right-frame/SkeletonRightFrame";
import { PageDefaultContainer } from "../containers/PageDefaultContainer";
import { WideContainerWithTopBar } from "../containers/WideContainerWithTopBar.container";
import { useGenerateContentSubmission } from "../utils/useGenerateContentSubmission";

const QUERY = graphql`
	query NodeScreen_Query($learnOpportunityId: ID!) {
		node(id: $learnOpportunityId) {
			id
			... on LearnOpportunityV2 {
				id
				...NodeScreen_NodeFragment
			}
		}
		Viewer {
			Auth {
				currentUser {
					user {
						extension(userExtensionKind: InfectopharmUserExtension) {
							... on InfectopharmUserExtension {
								globalUserStatus
								country
							}
						}
						...DefaultContainer_UserDataFragment
						...UserStatusMessage_UserFragment
					}
				}
			}
		}
	}
`;

const LEARN_OPPORTUNITY_FRAGMENT = graphql`
	fragment NodeScreen_NodeFragment on LearnOpportunityV2 {
		id
		structureDefinition {
			title
		}
		root {
			id
			structureDefinition {
				title
				... on LearnOpportunityRootStructureDefinition {
					extension {
						... on LearnOpportunityRootExtensionImpl {
							configConsequences {
								rewardExpired
								showAnswerExpired
							}
						}
					}
				}
			}
			...showAnswerButtonContainer_NodeFragment
		}
		typeDefinition {
			... on LearnOpportunityELearningContentTypeDefinition {
				containedElementTypes
				contentNodeAdvancementResult {
					status
				}
			}
			... on LearnOpportunityBranchTypeDefinition {
				children {
					id
					typeDefinition {
						... on LearnOpportunityELearningContentTypeDefinition {
							containedElementTypes
							contentNodeAdvancementResult {
								status
							}
						}
					}
				}
			}
			definitionType
		}
		...useGenerateContentSubmission_NodeFragment
		...showAnswerButtonContainer_NodeFragment
	}
`;

export function NodeScreen() {
	const {
		params: { learnOpportunityId },
	} = useMatch(getLearnOpportunityPath(LEARN_OPPORTUNITY_ID_URL_PARAM))!;
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [isModulExpiredAndFailed, setIsModuleExpiredAndFailed] = useState(false);
	const query = useLazyLoadQuery<NodeScreen_Query>(
		QUERY,
		{
			learnOpportunityId: learnOpportunityId! as string,
		},
		{ fetchPolicy: "network-only" },
	);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_, loadQuery] = useQueryLoader<NodeScreen_Query>(QUERY);
	const contentSubmissionProgressPercentage = useSelector(selectProgressBar);
	useEffect(() => {
		loadQuery(
			{ learnOpportunityId: learnOpportunityId! as string },
			{ fetchPolicy: "network-only" },
		);
	}, [contentSubmissionProgressPercentage, learnOpportunityId, loadQuery]);

	const learnOpportunity = useFragment<NodeScreen_NodeFragment$key>(
		LEARN_OPPORTUNITY_FRAGMENT,
		query.node,
	)!;

	if (!learnOpportunity) {
		return <Navigate to="*" />;
	}

	usePageTitle(learnOpportunity.structureDefinition.title);

	const [isReady, setIsReady] = useState(false);
	const extractFirstContentNodeId = () => {
		const firstChild = learnOpportunity.typeDefinition.children?.[0];
		return firstChild?.id || learnOpportunity.id;
	};
	const isModuleExpired =
		learnOpportunity?.root?.structureDefinition.extension?.configConsequences?.showAnswerExpired;
	const nodeId = learnOpportunityId || learnOpportunity.id;
	const generateNodeContentSubmission = useGenerateContentSubmission({
		learnOpportunity,
		loadQuery,
		setIsReady,
		nodeId,
	});

	const containedElementTypes = learnOpportunity?.typeDefinition.containedElementTypes;
	useEffect(() => {
		generateNodeContentSubmission();
	}, []);

	return (
		<PageDefaultContainer
			collapsed={false}
			screenName="learn-opportunity"
			containerId={learnOpportunity.id}
		>
			<WideContainerWithTopBar className="pr-0 pl-2">
				<div className="flex flex-column lg:flex-row">
					{}
					<NodeLeftFrame
						isContentNodeScreen={false}
						rootId={learnOpportunity?.root?.id!}
						ignoreUserLearnState={false}
					/>
					<DescriptionAndAttachmentsSideBar className="col-12 xl:col-8 p-0 flex flex-column flex-grow-1">
						{query.Viewer.Auth.currentUser?.user.extension?.globalUserStatus ===
							"initialValidation" && (
							<ProjectUserStatusMessageComponent user={query.Viewer.Auth.currentUser?.user} />
						)}
						<ShowAnswerButtonContainer
							isContentSubmissionScreen={false}
							hasMultipleChoice={containedElementTypes?.includes("multipleChoice") || false}
							disabled={!isModuleExpired}
							learnOpportunityFragmentRef={learnOpportunity}
						/>
						<Suspense fallback={<SkeletonRightFrame />}>
							{isReady && (
								<RightFrameContainer
									setIsModuleExpiredAndFailed={setIsModuleExpiredAndFailed}
									contentNodeId={extractFirstContentNodeId()}
								/>
							)}
						</Suspense>
					</DescriptionAndAttachmentsSideBar>
				</div>
			</WideContainerWithTopBar>
		</PageDefaultContainer>
	);
}

const DescriptionAndAttachmentsSideBar = styled.div`
	padding-bottom: 200px;
	@media only screen and (min-width: 1020px) {
		overflow: hidden;
	}
`;
